import { Box, ButtonBase, type SxProps, type Theme } from '@mui/material';
import { CopyButton } from './CopyButton';

interface RegistrationPlateProps {
  registrationNumber: string;
  sx?: SxProps<Theme>;
  /** When true, show a copy-to-clipboard control next to the plate (#2671). */
  copyable?: boolean;
  /** Overrides the copy button's accessible name. */
  copyLabel?: string;
  /** Makes the plate itself a button, e.g. to open the vehicle card (#3400). */
  onClick?: () => void;
  /** Accessible name of that button. */
  openLabel?: string;
}

export function RegistrationPlate({
  registrationNumber,
  sx,
  copyable = false,
  copyLabel,
  onClick,
  openLabel,
}: RegistrationPlateProps) {
  const plateFace = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'stretch',
        height: 22,
        borderRadius: '4px',
        border: '1.5px solid',
        borderColor: 'grey.400',
        overflow: 'hidden',
        bgcolor: '#fff', // design-allow-hex: physical license plate is always white, not themeable
        verticalAlign: 'middle',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {/* EU blue strip */}
      <Box
        component="span"
        sx={{
          width: 8,
          bgcolor: '#003399', // design-allow-hex: EU plate color, fixed legal blue
          flexShrink: 0,
        }}
      />
      {/* Registration number */}
      <Box
        component="span"
        sx={{
          px: 0.75,
          display: 'inline-flex',
          alignItems: 'center',
          fontWeight: 700,
          fontFamily: '"Roboto Mono", monospace',
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
          color: '#1a1a1a', // design-allow-hex: physical license plate text is always black, not themeable
          lineHeight: 1,
        }}
      >
        {registrationNumber.toUpperCase()}
      </Box>
    </Box>
  );

  // A clickable plate is a real button, so it is focusable and announced.
  const plate = onClick ? (
    <ButtonBase
      onClick={onClick}
      aria-label={openLabel}
      sx={{ borderRadius: '4px', display: 'inline-flex' }}
    >
      {plateFace}
    </ButtonBase>
  ) : plateFace;

  if (!copyable) {
    return plate;
  }

  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {plate}
      <CopyButton
        value={registrationNumber.toUpperCase()}
        {...(copyLabel !== undefined ? { label: copyLabel } : {})}
      />
    </Box>
  );
}
